import React, { FC, MouseEvent, KeyboardEvent, isValidElement } from 'react';
import { IconProps, Icon } from '../components/Icon';
import { Box, Button, ButtonGroup, CloseButton, HStack, useMultiStyleConfig, UseToastOptions } from '@chakra-ui/react';
import { Typography } from '../components/Typography';

type AlertVariants = 'success' | 'error' | 'info' | 'warning' | 'neutral';

export type Props = Pick<UseToastOptions, 'isClosable'> & {
  variant: AlertVariants;
  title?: string;
  description: string | React.JSX.Element;
  actions?: {
    primary?: {
      label: string;
      onClick: (e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => void;
    };
    secondary?: {
      label: string;
      onClick: (e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => void;
    };
  };
  onClose?: (e?: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => void;
  showIcon?: boolean;
  role?: 'alert' | 'status';
};

export const Alert: FC<Props> = ({
  variant,
  title,
  description,
  actions,
  isClosable,
  onClose,
  showIcon,
  role = 'status',
}) => {
  const variantToIcon: Record<AlertVariants, IconProps> = {
    success: {
      prefix: 'fas',
      iconName: 'check-circle',
    },
    error: {
      prefix: 'fas',
      iconName: 'octagon-exclamation',
    },
    info: {
      prefix: 'fas',
      iconName: 'info-circle',
    },
    warning: {
      prefix: 'fas',
      iconName: 'exclamation-triangle',
    },
    neutral: {
      prefix: 'far',
      iconName: 'bell',
    },
  };

  /**
   * Hooks
   */
  const styles = useMultiStyleConfig('Alert', { variant });
  /**
   * DOM
   */
  return (
    <HStack data-object-type="alert" role={role} align="flex-start" __css={styles.container} display="inline-flex">
      {showIcon && (
        <Box __css={{ '& > span': styles.icon }}>
          <Icon {...variantToIcon[variant]} />
        </Box>
      )}
      <Box flexGrow={1}>
        {title && (
          <Box __css={styles.title}>
            <Typography textStyle={'h4'} as={'h2'}>
              {title}
            </Typography>
          </Box>
        )}
        <Box __css={styles.description}>
          {(isValidElement(description) && description) || <Typography textStyle={'p2'}>{description}</Typography>}
        </Box>
        {(actions?.primary || actions?.secondary) && (
          <ButtonGroup spacing={'2'} mt={'3'} colorScheme={'gray'}>
            {actions.primary && (
              <Button variant={'solid'} onClick={actions.primary.onClick}>
                {actions.primary.label}
              </Button>
            )}
            {actions.secondary && (
              <Button variant={'outline'} onClick={actions.secondary.onClick}>
                {actions.secondary.label}
              </Button>
            )}
          </ButtonGroup>
        )}
      </Box>
      {isClosable && <CloseButton size={'sm'} onClick={onClose} colorScheme={variant} variant={'ghost'} />}
    </HStack>
  );
};
